import React, { useState, useEffect, useCallback } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button as RabbitButton, Checkbox } from '@mesh.ai/rabbit';
import { EngagementQuestionCard } from 'Deer/components/Engagement/SurveyFormFilling/components/survey-form/EngagementQuestionCard';
import { ScrollableQuestionnaire } from 'Deer/components/Engagement/SurveyFormFilling/components/survey-form/ScrollableQuestionnaire';
import { forceRefreshNotificationCount } from 'actions/hellobar';
// import logger from 'use-reducer-logger';
import SkeletonLoader from 'components/SkeletonLoader';
import { useMatchedRoute } from 'components/hooks/useMatchedRoute';
import useReducerWithThunk from 'components/hooks/useReducerWithThunk';
import { useSnackbar } from 'components/hooks/useSnackbar';
import { useDispatch } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { FlexContainer, SpaceBetweenContainer, VerticalFlexContainer, StyledButton } from 'styles/common';
import { debounce } from 'throttle-debounce';

import { ArrowBack } from 'assets/assetsV1/iconComponents';

import { ENGAGEMENT_ANSWER_TYPES, HELLO_BAR_PAGE_TYPE_2 } from 'constants/ProjectConstants';
import { AnonymousIcon } from 'constants/icons';
import {
  sendClickedBackEngagementSurveyEvent,
  sendClickedNextEngagementSurveyEvent,
  sendSubmittedSurveyFormEvent,
} from 'constants/mixpanelHelper';
import { routes } from 'constants/routes';
import theme from 'constants/theme';

import { submitSurvey } from 'services/engagement';

import DateComp from 'uiToolkit/DateComp';
import QuestionStepper from 'uiToolkit/QuestionStepper';
import { BreadCrumb, BreadcrumbItem, BreadcrumbLink } from 'uiToolkitV1/BreadCrumb';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyLarge, TextLabel } from 'uiToolkitV1/Typography';

import {
  addSurveyDetailsAction,
  moveToNextQuestionAction,
  moveToPreviousQuestionAction,
  moveToQuestion,
  addPreviewSurveyDetailsAction,
} from './pulseSurveyActions';
import pulseSurveyReducer, { pulseSurveyInitialState } from './pulseSurveyReducer';

const Wrapper = styled(FlexContainer)`
  flex-direction: column;
  height: calc(100vh - 9vh);
  position: relative;
  top: 0.5vh;
  background: white;
  border-radius: 4px;
  box-shadow:
    0px 2px 1px -1px rgba(0, 0, 0, 0.2),
    0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12);
`;

const HeaderWrapper = styled(SpaceBetweenContainer)`
  height: 5rem;
  padding: 1rem 1rem;
  align-items: center;
  box-shadow: 1px 0px 1px 1px rgb(0 0 0 / 13%);
`;

const FooterWrapper = styled(SpaceBetweenContainer)`
  height: 4.5rem;
  padding: 1rem 1.5rem;
  align-items: center;
  box-shadow: 1px 0px 1px 1px rgb(0 0 0 / 13%);
`;

const CustomStyledButton = styled(StyledButton)`
  &.MuiButton-root.Mui-disabled {
    color: white;
  }
`;

// *********************** helpers START
const getQuestionCardComps = (questionsData, dispatch, surveyId, isPreview) =>
  questionsData.map((question, index) => ({
    id: question.id,
    comp: (
      <EngagementQuestionCard
        surveyId={surveyId}
        serialNumber={index + 1}
        question={question}
        dispatch={dispatch}
        isPreview={isPreview}
      />
    ),
  }));

const getStepperData = (questionsData) =>
  questionsData.map(
    ({ id, isRequired, isTextRequired, isVisited, selectedAnswerOptions, feedbackText, answerType }) => {
      let isAnswered = true;

      if (isRequired || isTextRequired) {
        if (answerType === ENGAGEMENT_ANSWER_TYPES.NONE) isAnswered = feedbackText !== null;
        else if (isTextRequired)
          isAnswered = isRequired ? selectedAnswerOptions.length !== 0 && feedbackText !== null : feedbackText !== null;
        else isAnswered = isRequired && selectedAnswerOptions.length !== 0;
      }

      return {
        id,
        isVisited,
        isRequired: isRequired || isTextRequired,
        isAnswered,
      };
    }
  );
// *********************** helpers END

// Used only in m-site
export default function SurveyQuestionnairePage({ isPreview = false, templateId }) {
  const { showSuccessSnackbar } = useSnackbar();

  const { pageName } = useMatchedRoute();

  const match = useRouteMatch();
  const history = useHistory();
  const { surveyId } = match.params;
  const [forceUpdateNum, setForceUpdateNum] = useState(0);
  const [acceptSubmission, setAcceptSubmission] = useState(false);

  const [state, dispatch] = useReducerWithThunk(
    // logger(pulseSurveyReducer),
    pulseSurveyReducer,
    pulseSurveyInitialState
  );
  const reduxDispatch = useDispatch();

  const {
    endDateFormatted,
    name,
    questionsData,
    currentQuestionId,
    isAnonymous,
    requiredQuestionsCount,
    answeredRequiredQuesCount,
  } = state;

  const isLastQuestion = currentQuestionId === questionsData?.[questionsData.length - 1].id;
  const remainingRequiredQuestionCount = requiredQuestionsCount - answeredRequiredQuesCount;
  const submissionEnabled = remainingRequiredQuestionCount === 0 && acceptSubmission;

  // *********** key press events START
  const handleKeyNavigation = useCallback((event) => {
    if (['TEXTAREA', 'INPUT'].includes(event.target.nodeName)) {
      return;
    }
    const key = event.key || event.code || 0;
    if (['ArrowUp', 'ArrowLeft'].includes(key)) {
      event.preventDefault();
      dispatch(moveToPreviousQuestionAction());
    } else if (['ArrowDown', 'ArrowRight'].includes(key)) {
      event.preventDefault();
      dispatch(moveToNextQuestionAction());
    }
  }, []);

  useEffect(() => {
    const debouncedForceUpdate = debounce(500, () => {
      setForceUpdateNum((num) => num + 1);
    });
    document.body.addEventListener('keydown', handleKeyNavigation, true);
    window.addEventListener('resize', debouncedForceUpdate);
    return () => {
      document.body.removeEventListener('keydown', handleKeyNavigation);
      window.removeEventListener('resize', debouncedForceUpdate);
    };
  }, [handleKeyNavigation]);
  // *********** key press events END

  const questionCards = questionsData ? getQuestionCardComps(questionsData, dispatch, surveyId, isPreview) : [];

  useEffect(() => {
    // We fetch API only when surveyId is available. Else we might have templateId for Preview Mode.
    if (surveyId) dispatch(addSurveyDetailsAction(surveyId));
  }, [surveyId]);

  useEffect(() => {
    // In case templateId is available, we fetch quesitons from API and populate the state with some dummy data along with the questions.
    templateId && dispatch(addPreviewSurveyDetailsAction(templateId));
  }, [templateId]);

  const handleSubmitSurvey = () => {
    if (isLastQuestion) {
      // We call api only when it's not in preview mode.
      if (isPreview) {
        showSuccessSnackbar({ message: 'This can not be submitted as it is a preview.' });
      } else {
        submitSurvey({ surveyId })
          .then(() => {
            reduxDispatch(forceRefreshNotificationCount({ param: HELLO_BAR_PAGE_TYPE_2.ENGAGEMENT }));
            showSuccessSnackbar({ message: 'Survey submitted successfully' });

            // Send tracking Events
            sendSubmittedSurveyFormEvent({
              eventProperties: {
                surveyId,
                success: true,
              },
            });

            history.push(routes.engagement.index);
          })
          .catch(() => {
            sendSubmittedSurveyFormEvent({
              eventProperties: {
                surveyId,
                success: false,
              },
            });
          });
      }
    } else {
      dispatch(moveToNextQuestionAction());
      sendClickedNextEngagementSurveyEvent();
    }
  };

  const backClickHandler = () => {
    history.goBack();
  };

  if (!questionsData) {
    return (
      <>
        <SkeletonLoader skeletons={['100%', '100%']} />;
        <SkeletonLoader skeletons={['100%', '100%']} />;
      </>
    );
  }

  return (
    <Wrapper leftGutter={1} rightGutter={1}>
      <HeaderWrapper>
        <Flex alignItems="center">
          <Button StartIcon={ArrowBack} variant="tertiary" iconSize="32px" onClick={backClickHandler} />
          <Flex ml="24px" flexDir="column">
            <BreadCrumb>
              <BreadcrumbItem>
                <BreadcrumbLink to={routes.engagement.index}>My Surveys</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink to={routes.engagement.index}>Active Surveys</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink>{name}</BreadcrumbLink>
              </BreadcrumbItem>
            </BreadCrumb>
            <TextBodyLarge mt="2px" bold>
              {name}
            </TextBodyLarge>
          </Flex>
        </Flex>

        <DateComp
          editable={false}
          startDateTime={endDateFormatted.timeStamp}
          color={endDateFormatted.color}
          isCrossed={endDateFormatted.isCrossed}
        />
      </HeaderWrapper>

      <ScrollableQuestionnaire
        comps={questionCards}
        selectedCompId={currentQuestionId}
        forceUpdateNum={forceUpdateNum}
      />

      <Box
        data-testid="survey-questionnaire-footer"
        w="100%"
        display="flex"
        justifyContent="space-between"
        height={isLastQuestion ? '8.5rem' : '8rem'}
        alignItems="center"
        style={{
          background: '#f5f7f9',
          boxShadow: 'rgb(207, 215, 223) 0px -1px 0px 0px',
        }}
        flexDir="column"
        padding="8px"
      >
        <VerticalFlexContainer
          style={{
            alignSelf: 'flex-end',
            transform: 'scale(0.8)',
          }}
        >
          <Box
            sx={{
              // select first div
              '> div': {
                justifyContent: 'flex-end',
              },
            }}
          >
            <TextLabel color="text.g2" style={{ 'margin-right': '4px', textAlign: 'right' }}>
              {remainingRequiredQuestionCount} question
              {remainingRequiredQuestionCount > 1 ? 's' : ''} remaining
            </TextLabel>

            <QuestionStepper
              currentItemId={currentQuestionId}
              data={getStepperData(questionsData)}
              clickHandler={(id) => dispatch(moveToQuestion(id))}
            />
          </Box>
        </VerticalFlexContainer>
        <Flex
          flexDir="column"
          style={{
            width: '100%',
          }}
          data-testid="survey-questionnaire-footer-buttons"
          padding="0px 16px"
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {isLastQuestion ? (
            <Box mb="8px" style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                id="pulse_submit_checkbox"
                checked={acceptSubmission}
                color="primary"
                onCheckedChange={() => setAcceptSubmission((val) => !val)}
              />
              <Box mx="16px" as="label" style={{ cursor: 'pointer' }} htmlFor="pulse_submit_checkbox">
                <TextLabel
                  style={{
                    userSelect: 'none',
                  }}
                >
                  Are you sure you want to submit your response?
                </TextLabel>
              </Box>
            </Box>
          ) : isAnonymous ? (
            <Box mb="8px" ml="16px" style={{ display: 'flex', alignItems: 'center' }}>
              <AnonymousIcon color={theme.palette.grey.main} size="tiny" rightGutter={2} />
              <TextLabel color="text.g2">You are anonymous</TextLabel>
            </Box>
          ) : null}
          <Flex
            style={{
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <RabbitButton
              variant="transparentOutline"
              onClick={() => {
                sendClickedBackEngagementSurveyEvent();
                dispatch(moveToPreviousQuestionAction());
              }}
              style={{ minWidth: '120px' }}
            >
              Back
            </RabbitButton>
            <RabbitButton
              variant="filled"
              onClick={handleSubmitSurvey}
              disabled={isLastQuestion && !submissionEnabled}
              style={{ minWidth: '120px', marginLeft: '16px' }}
            >
              {isLastQuestion ? 'Submit' : 'Next '}
            </RabbitButton>
          </Flex>
        </Flex>
      </Box>
    </Wrapper>
  );
}
